import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Button } from '../../ui/button';
import { CheckCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/store';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Cookies from 'js-cookie';
import { TOKEN_NAME } from '../../../constants';
import { doPerformOrderAction } from '../../../modules/orders/store/thunkts';

const CancelledOrders = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const [cancelledOrders, setCancelledOrders] = useState([]);

  useEffect(() => {
    fetchCancelledOrders();
  }, []);

  const fetchCancelledOrders = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/dashboard/cancelled-orders`, {
        headers: {
          Authorization: `Bearer ${Cookies.get(TOKEN_NAME)}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const res = await response.json();
      setCancelledOrders(res.orders);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveOrder = async (orderId: string, action: 'PROCESSED_CANCELLED', e: React.MouseEvent) => {
    e.stopPropagation();
    await dispatch(doPerformOrderAction({ orderId, action }));
    fetchCancelledOrders();
  };

  const sortedCancelledOrders = useMemo(
    () =>
      [...cancelledOrders]
        ?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 10),
    [cancelledOrders],
  );

  const handleRowClick = (orderId: string, e: React.MouseEvent) => {
    const directTo = `/orders/${orderId}`;
    if (e.ctrlKey || e.metaKey) {
      window.open(directTo, '_blank');
    } else {
      history.push(directTo);
    }
  };

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle>Cancelled orders</CardTitle>
        <CardDescription>
          Automatically cancelled orders due to inactivity. Please acknowledge that you have processed these orders.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Order ID</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Delivery Address</TableHead>
              <TableHead className="text-right">Processed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cancelledOrders?.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="text-center text-gray-500 pt-12">
                  No cancelled orders that need your attention, enjoy your day!
                </TableCell>
              </TableRow>
            ) : (
              <AnimatePresence mode="popLayout">
                {sortedCancelledOrders.map((order) => (
                  <motion.tr
                    key={order.orderId}
                    initial={{ opacity: 1, height: 'auto' }}
                    exit={{
                      opacity: 0,
                      height: 0,
                      transition: {
                        duration: 0.2,
                        ease: 'easeOut',
                      },
                    }}
                    onClick={(e) => handleRowClick(order.orderId, e)}
                    className="hover:cursor-pointer hover:bg-gray-50"
                    style={{ display: 'table-row' }}
                    layout
                  >
                    <TableCell className="font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                      <div>{order.orderId}</div>
                    </TableCell>
                    <TableCell className="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
                      <div>{order.orderRef}</div>
                    </TableCell>
                    <TableCell
                      title={order.deliveryAddress}
                      className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                      <div>{order.deliveryAddress}</div>
                    </TableCell>
                    <TableCell className="text-right overflow-hidden text-ellipsis whitespace-nowrap">
                      <div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => handleRemoveOrder(order.orderId, 'PROCESSED_CANCELLED', e)}
                              >
                                <CheckCheck />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Processed</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                  </motion.tr>
                ))}
              </AnimatePresence>
            )}
          </TableBody>
          {cancelledOrders?.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={3}>Total Cancelled orders</TableCell>
                <TableCell className="text-right">{cancelledOrders?.length}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </CardContent>
    </Card>
  );
};

export default CancelledOrders;
